import React, { useMemo, useState } from "react";
import {
    Await,
    Form,
    Link,
    useAsyncValue,
    useFetcher,
    useLoaderData,
} from "react-router-dom";
import { formatDistance } from "date-fns";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import GroupIcon from "@mui/icons-material/Group";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from '@mui/icons-material/Edit';

import { GetAddressBooksResponseItem } from "services/apiService";
import AppNavigation from "router/AppNavigation";
import FileInput from "components/FileInput";
import { useSnackbar } from "notistack";
import { LoadingButton } from '@mui/lab';

interface CreateAddressBookFormProps {
    open: boolean;
    onClose: () => void;
}

export enum AddressBookListAction {
    CreateAddressBook = "CREATE_ADDRESSBOOK",
    DeleteAddressBook = "DELETE_ADDRESSBOOK",
    UpdateAddressBook = "UPDATE_ADDRESSBOOK",
}

const AddressBookListItem = ({
    addressBook,
}: {
    addressBook: GetAddressBooksResponseItem;
}) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const fetcher = useFetcher();
    const { enqueueSnackbar } = useSnackbar();

    React.useEffect(() => {
        if (fetcher.data) {
            console.log("fetcher.data : ", fetcher.data)
            if (fetcher.data.success) {
                switch (fetcher.data.action) {
                    case AddressBookListAction.DeleteAddressBook:
                        console.log("fetcher success")
                        enqueueSnackbar("AddressBook deleted successfully!", {
                            variant: "success",
                        });
                        handleClose();
                        break;

                    case AddressBookListAction.UpdateAddressBook:
                        console.log("fetcher success")
                        enqueueSnackbar("AddressBook updated successfully!", {
                            variant: "success",
                        });
                        handleClose();
                        break;
                    default:
                        break;
                }

            } else {
                console.log("fetcher error")
                enqueueSnackbar(fetcher.data.error, { variant: "error" });
            }
        }
    }, [fetcher.data, enqueueSnackbar]);

    return (
        <>
            <ListItem
                disablePadding
                component={Paper}
                variant="outlined"
                secondaryAction={
                    <IconButton aria-label="more" onClick={handleClick}>
                        <MoreHorizIcon fontSize="inherit" />
                    </IconButton>
                }
            >
                <ListItemButton
                    component={Link}
                    to={AppNavigation.AddressBookDetail.replace(
                        ":addressBookId",
                        addressBook.id
                    )}
                >
                    <ListItemIcon>
                        <GroupIcon style={{ color: '#226CE6 ' }} />
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography variant="inherit" noWrap>
                                {addressBook.name}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="inherit" noWrap>
                                {formatDistance(
                                    new Date(addressBook.createdOnUtc),
                                    new Date(),
                                    {
                                        addSuffix: true,
                                    }
                                )}
                            </Typography>
                        }
                    />

                    <Box sx={{ display: "flex", alignItems: "start", mx: 4 }}>
                        <GroupIcon sx={{ mr: 0.5 }} />
                        <span>{addressBook.contactsAmount}</span>
                    </Box>
                </ListItemButton>
            </ListItem>

            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem>
                    <ListItemIcon>
                        <fetcher.Form method="delete" id="deleteAddressBookForm">
                            <LoadingButton
                                aria-label="delete"
                                type="submit"
                                form="deleteAddressBookForm"
                                startIcon={<DeleteIcon />}
                                loading={fetcher.state === "submitting"}
                                color="error"
                            >
                                Delete

                            </LoadingButton>
                            <input
                                type="hidden"
                                name="action"
                                value={AddressBookListAction.DeleteAddressBook}
                            />
                            <input
                                type="hidden"
                                name="addressBookId"
                                value={addressBook.id}
                            />
                        </fetcher.Form>
                    </ListItemIcon>

                </MenuItem>
                <MenuItem>
                    <ListItemIcon>
                        <fetcher.Form method="put" id="updateAddressBookForm">
                            <LoadingButton
                                aria-label="update"
                                // type="submit"
                                form="updateAddressBookForm"
                                // loading={fetcher.state === "submitting"}
                                startIcon={<EditIcon />}
                                color="info"
                                onClick={handleClose}
                            >
                                Update
                            </LoadingButton>
                            <input
                                type="hidden"
                                name="action"
                                value={AddressBookListAction.UpdateAddressBook}
                            />
                            <input
                                type="hidden"
                                name="addressBookId"
                                value={addressBook.id}
                            />
                        </fetcher.Form>
                    </ListItemIcon>

                </MenuItem>
            </Menu>
        </>
    );
};

const renderAddressBookRow = ({
    data,
    index,
    style,
}: ListChildComponentProps<GetAddressBooksResponseItem[]>) => {
    const addressBook = data[index];

    return (
        <div key={addressBook.id} style={style}>
            <AddressBookListItem addressBook={addressBook} />
        </div>
    );
};

const AddressBookList = ({
    addressBooks,
}: {
    addressBooks: GetAddressBooksResponseItem[];
}) => {
    return addressBooks.length > 0 ? (
        <div style={{ display: "flex", flexGrow: 1 }} className="address-book-list">
            <div style={{ flex: "1 1 auto" }}>
                <AutoSizer>
                    {({ height, width }) => (
                        <FixedSizeList
                            height={height}
                            width={width}
                            itemSize={88}
                            itemCount={addressBooks.length}
                            itemData={addressBooks}
                        >
                            {renderAddressBookRow}
                        </FixedSizeList>
                    )}
                </AutoSizer>
            </div>
        </div>
    ) : (
        <div>No address books</div>
    );
};

const CreateAddressBookForm = ({
    open,
    onClose,
}: CreateAddressBookFormProps) => {
    const fetcher = useFetcher();
    const { enqueueSnackbar } = useSnackbar();

    React.useEffect(() => {
        if (fetcher.data) {
            if (fetcher.data.success === true) {
                enqueueSnackbar("AddressBook created successfully!", {
                    variant: "success",
                });
                onClose();
            }
        }
    }, [fetcher.data, enqueueSnackbar]);

    // Close dialog when fetcher data changes
    // React.useEffect(() => {
    //   onClose();
    // }, [fetcher.data, onClose]);
    const [loadSaveAddressBook, setLoadSaveAddressBook] = useState(false)

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>New address book</DialogTitle>

            <DialogContent>
                <fetcher.Form method="post" id="createNewAddressBookForm">
                    <TextField
                        name="name"
                        required
                        autoComplete="off"
                        label="Name"
                        autoFocus
                        size="small"
                        fullWidth
                        sx={{ my: 1 }}
                    />

                    {/* <FileInput
            placeholder="Upload contacts"
            inputProps={{ accept: "text/csv" }}
            size="small"
            fullWidth
            sx={{ my: 1 }}
          /> */}
                    <input
                        type="hidden"
                        name="action"
                        value={AddressBookListAction.CreateAddressBook}
                    />
                </fetcher.Form>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <LoadingButton loading={fetcher.state === "submitting"} type="submit" form="createNewAddressBookForm">
                    Create
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

const AddressBookListWithForm = () => {
    const addressBooks = useAsyncValue() as GetAddressBooksResponseItem[];
    const [searchText, setSearchText] = useState("");
    const filteredAddressBooks = useMemo(() => {
        if (searchText.length === 0) return addressBooks;

        return addressBooks.filter((addressBook) =>
            addressBook.name
                .toLocaleLowerCase()
                .includes(searchText.toLocaleLowerCase())
        );
    }, [addressBooks, searchText]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Grid container alignItems="center" spacing={2} sx={{ marginBottom: 2 }}>
                <Grid item xs={12} md={8}>
                    <TextField
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        placeholder="Search"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} md={4} display="flex" justifyContent="end">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreateDialogOpen(true)}
                    >
                        Add
                    </Button>
                </Grid>
            </Grid>

            <AddressBookList addressBooks={filteredAddressBooks} />

            <CreateAddressBookForm
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
            />
        </Box>
    );
};

const AddressBooks = () => {
    const loaderData = useLoaderData() as {
        addressBooks: Promise<GetAddressBooksResponseItem[]>;
    };

    return (
        <React.Suspense
            fallback={
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress />
                    <p>Fetching address books...</p>
                </Box>
            }
        >
            <Await
                resolve={loaderData.addressBooks}
                errorElement={<p>Error while loading STATS data</p>}
            >
                <AddressBookListWithForm />
            </Await>
        </React.Suspense>
    );
};

export default AddressBooks;
